import './shootHolds.css';
// Overview per mockups/01-overview.html: greeting, needs-attention list
// (overdue invoices, lead follow-ups due, tasks due today + overdue with
// working checkboxes), vitals strip, active-project gallery, dashboards +
// activity two-up.

import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode, type MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import { useMoneyMode } from '../../lib/money';
import { filterTargetsByPeriod } from '../../lib/targets';
import { calendarMonthName, melbourneClock } from '../../../shared/dates';
import { GOAL_METRICS, type GoalMetric } from '../../../shared/stats';
import { ActivityRow, formatDayMonth, formatMoney, Group, GroupLink, PageHeader, Pill, ProjectCard, Vital, Vitals } from '../../components/primitives';
import { AlertIcon, SettingsIcon } from '../../components/icons';
import { QuickAddRow, TaskRow } from '../../components/TaskRow';
import { useIsMobile } from '../../lib/useIsMobile';
import { useOverviewData } from '../../lib/useOverviewData';
import { DEFAULT_OVERVIEW_TARGETS_SETTINGS, isOverviewTargetsSettings, type OverviewTargetsSettings, type OverviewVital } from '../../../shared/overview';
import { OverviewChooser, VITAL_LABELS } from '../../components/OverviewChooser';
import type { OverviewPayload } from './types';
import { greeting, compactMoney, targetPeriodAbbr, relTime } from './helpers';
import { WeekCalendar } from './WeekCalendar';
import { TargetsBand } from './TargetsBand';
import { InvoiceAttentionRow } from './InvoiceAttentionRow';
import { FollowupAttentionRow } from './FollowupAttentionRow';
import { LockedInAttentionRow } from './LockedInAttentionRow';
import { ProjectCarousel } from './ProjectCarousel';
import { MorningBriefPanel } from './MorningBriefPanel';
import { JournalSection } from './JournalSection';

export default function Overview({ userId }: { userId: number }) {
  const { data, refresh, onTaskRemoved, refreshNotice, fetchedAt } = useOverviewData<OverviewPayload>(userId);
  const mode = useMoneyMode();
  const isMobile = useIsMobile();

  const [chooser, setChooser] = useState<'vitals' | 'targets' | null>(null);
  const chooserTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [chooserSaving, setChooserSaving] = useState(false);
  const choose = (kind: 'vitals' | 'targets'): MouseEventHandler<HTMLButtonElement> => event => {
    if (chooserSaving) return;
    chooserTriggerRef.current = event.currentTarget;
    setChooser(chooser === kind ? null : kind);
  };
  const closeChooser = useCallback(() => setChooser(null), []);
  const [savedSettings, setSavedSettings] = useState<OverviewTargetsSettings | null>(null);
  const onSettingsSaved = (next: OverviewTargetsSettings) => { setSavedSettings(next); refresh(); };
  // A locally saved choice wins until the aggregate refresh delivers the same settings.
  useEffect(() => { setSavedSettings(null); }, [data?.targets_settings]);
  const cachedLabel = fetchedAt ? `Cached ${melbourneClock(fetchedAt)}` : 'Cached';
  const notice = refreshNotice && <div className="inline-notice" role="status"><span className="dot" /><span className="msg">
    {fetchedAt ? <>Couldn't refresh. Showing cached data from <span className="when">{melbourneClock(fetchedAt)}</span>.</> : refreshNotice}
    </span><button type="button" className="act" onClick={refresh}>Retry</button></div>;
  if (!data) return <><PageHeader title="Overview" liveError={!!refreshNotice} liveLabel={refreshNotice ? 'Could not refresh' : 'Loading'} /><div className="page-body"><div className="content">{notice}</div></div></>;

  const { counts, finance } = data;
  const targetsSettings = savedSettings ?? (isOverviewTargetsSettings(data.targets_settings) ? data.targets_settings : DEFAULT_OVERVIEW_TARGETS_SETTINGS);
  const overdueDisplay = mode === 'ex' ? finance.overdue_ex_cents : finance.overdue_cents;
  const outstandingDisplay = mode === 'ex' ? finance.outstanding_ex_cents : finance.outstanding_cents;
  const vitalValues: Record<OverviewVital, string> = {
    open_tasks: String(counts.open_tasks), active_projects: String(counts.active_projects),
    outstanding: formatMoney(outstandingDisplay), overdue: formatMoney(overdueDisplay),
    revenue_ex: compactMoney(data.goal_actuals?.month.revenue_ex ?? 0),
    leads: String(data.goal_actuals?.month.leads ?? 0), proposals_delivered: String(data.goal_actuals?.month.proposals_delivered ?? 0),
    jobs_won: String(data.goal_actuals?.month.jobs_won ?? 0),
  };
  const periodTargets = filterTargetsByPeriod(data.targets ?? [], targetsSettings);
  const targetValues = Object.fromEntries(GOAL_METRICS.map(metric => {
    const target = periodTargets.find(t => t.metric === metric && t.period === 'month') ?? periodTargets.find(t => t.metric === metric);
    const value = target ? `${target.metric === 'revenue_ex' ? compactMoney(target.actual) : target.actual} / ${target.metric === 'revenue_ex' ? compactMoney(target.target) : target.target}${target.carried_forward ? '*' : ''}` : vitalValues[metric];
    return [metric, value];
  }));
  const vitalTiles = targetsSettings.vitals.slice(0, targetsSettings.vitals_max).map(key => {
    const goal = GOAL_METRICS.includes(key as GoalMetric);
    const target = (data.targets ?? []).find(t => t.metric === key && t.period === 'month');
    const actual = data.goal_actuals?.month[key as GoalMetric] ?? 0;
    const sub = goal ? target && target.target > 0
      ? `${Math.round(actual / target.target * 100)}% of ${key === 'revenue_ex' ? compactMoney(target.target) : target.target}` : 'This month' : undefined;
    const label = goal ? `${key === 'revenue_ex' ? 'Revenue' : VITAL_LABELS[key]} · ${calendarMonthName(data.today, true)}` : VITAL_LABELS[key];
    return <Vital key={key} label={label} value={vitalValues[key]} sub={sub}
      tone={key === 'outstanding' || key === 'revenue_ex' ? 'gold' : key === 'overdue' ? 'danger' : key === 'active_projects' ? 'accent' : undefined}
      to={goal ? '/stats' : key === 'open_tasks' ? '/tasks' : key === 'active_projects' ? '/projects' : '/finance'} />;
  });

  // Greeting summary — only segments with something to say.
  const segments: ReactNode[] = [];
  if (counts.tasks_due_today > 0) {
    segments.push(
      <strong key="due">
        {counts.tasks_due_today} task{counts.tasks_due_today === 1 ? '' : 's'} due today
      </strong>,
    );
  }
  if (counts.tasks_overdue > 0) {
    segments.push(
      <span key="over" className="bad">
        {counts.tasks_overdue} overdue
      </span>,
    );
  }
  if ((finance.overdue_count ?? 0) > 0) {
    segments.push(
      <Fragment key="inv">
        <span className="bad">
          {finance.overdue_count} overdue invoice{finance.overdue_count === 1 ? '' : 's'}
        </span>{' '}
        <span className="money">{formatMoney(overdueDisplay)}</span>
      </Fragment>,
    );
  }

  const lockedInDue = data.locked_in_due ?? [];
  const attentionCount =
    data.overdue_invoices.length +
    data.followups.length +
    lockedInDue.length +
    data.attention_tasks.length +
    (counts.worker_errors > 0 ? 1 : 0);

  return (
    <>
      <PageHeader title="Overview" liveError={!!refreshNotice} liveLabel={refreshNotice ? cachedLabel : 'Synced'} />
      <div className="page-body">
        <div className="content">
          {notice}
          <div className="greeting">
            <h1>{greeting()}, Henry.</h1>
            <p>
              {segments.length === 0
                ? 'Nothing needs attention.'
                : segments.map((seg, i) => (
                    <Fragment key={i}>
                      {i > 0 && ' · '}
                      {seg}
                    </Fragment>
                  ))}
            </p>
          </div>

          <MorningBriefPanel data={data.morning_brief} today={data.today} onVoted={refresh} />

          <Group title="Needs attention" count={attentionCount}>
            {counts.worker_errors > 0 &&
              (isMobile ? (
                <Link className="row mrow-att" to="/settings">
                  <span className="ico d">
                    <AlertIcon />
                  </span>
                  <div className="main">
                    <div className="ttl">
                      {counts.worker_errors} worker error{counts.worker_errors === 1 ? '' : 's'} in
                      the last 24h
                    </div>
                    <div className="sub">
                      <span className="d">Error log →</span>
                    </div>
                  </div>
                </Link>
              ) : (
                <Link className="row" to="/settings">
                  <span className="row-icon ic-danger">
                    <AlertIcon />
                  </span>
                  <span
                    className="row-title"
                    title={`${counts.worker_errors} worker error${counts.worker_errors === 1 ? '' : 's'} in the last 24h`}
                  >
                    {counts.worker_errors} worker error{counts.worker_errors === 1 ? '' : 's'} in the
                    last 24h
                  </span>
                  <span className="row-spacer" />
                  <span className="row-meta danger">Error log →</span>
                </Link>
              ))}
            {data.overdue_invoices.map((inv) => (
              <InvoiceAttentionRow key={inv.id} inv={inv} today={data.today} mode={mode} onChanged={refresh} />
            ))}
            {data.followups.map((lead) => (
              <FollowupAttentionRow key={lead.id} lead={lead} today={data.today} onChanged={refresh} />
            ))}
            {lockedInDue.map((lead) => (
              <LockedInAttentionRow key={lead.id} lead={lead} today={data.today} />
            ))}
            {data.attention_tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                today={data.today}
                projectDisplay="context"
                onChanged={refresh}
                onRemoved={onTaskRemoved}
              />
            ))}
            <QuickAddRow
              hint="Add task for today…"
              defaults={{ due_date: data.today }}
              onAdded={refresh}
            />
            {attentionCount === 0 && (
              <div className="empty-note">All clear — nothing waiting on you.</div>
            )}
          </Group>

          <JournalSection />

          <div className="overview-chooser-anchor overview-vitals">
            <Vitals>{vitalTiles}<button type="button" className={`vital-choose${chooser === 'vitals' ? ' on' : ''}`} aria-label="Choose tiles" aria-haspopup="dialog" aria-expanded={chooser === 'vitals'} disabled={chooserSaving} onClick={choose('vitals')}><SettingsIcon /></button></Vitals>
            <div className="vitals-choose-m"><button type="button" className="choose-btn" aria-haspopup="dialog" aria-expanded={chooser === 'vitals'} disabled={chooserSaving} onClick={choose('vitals')}><SettingsIcon />Choose tiles</button></div>
            {chooser === 'vitals' && <OverviewChooser triggerRef={chooserTriggerRef} onSavingChange={setChooserSaving} kind="vitals" settings={targetsSettings} values={vitalValues} periods={Object.fromEntries(GOAL_METRICS.map(key => [key, [{ period: 'month', label: calendarMonthName(data.today, true) }]]))} onSaved={onSettingsSaved} onClose={closeChooser} />}
          </div>
          <div className="overview-chooser-anchor overview-targets">
            <TargetsBand targets={data.targets ?? []} today={data.today} settings={targetsSettings} previous={data.previous_targets} chooserOpen={chooser === 'targets'}
              chooserSaving={chooserSaving} onChoose={choose('targets')} onCopied={refresh} />
            {chooser === 'targets' && <OverviewChooser triggerRef={chooserTriggerRef} onSavingChange={setChooserSaving} kind="targets" settings={targetsSettings} values={targetValues} periods={Object.fromEntries(GOAL_METRICS.map(key => [key, (data.targets ?? []).filter(t => t.metric === key).map(t => ({ period: t.period, label: t.period === 'month' ? calendarMonthName(t.period_key, true) : targetPeriodAbbr(t.period, t.period_key) }))]))} onSaved={onSettingsSaved} onClose={closeChooser} />}
          </div>

          <WeekCalendar />

          <Group
            title="Active projects"
            count={counts.active_projects}
            link={<GroupLink to="/projects">All projects →</GroupLink>}
          >
            {isMobile ? (
              <ProjectCarousel
                count={data.projects.length}
                onHold={
                  counts.on_hold_count > 0 ? (
                    <Link className="overview-hold-link" to="/projects?phase=on_hold">
                      <span className="phase-dot pd-hold" />
                      <span>{counts.on_hold_count} on hold</span>
                    </Link>
                  ) : null
                }
              >
                {data.projects.map((p) => (
                    <ProjectCard
                      key={p.id}
                      phase={p.phase}
                      dueLabel={formatDayMonth(
                        p.phase === 'shoot' ? (p.shoot_date ?? p.due_date) : p.due_date,
                      )}
                      name={p.name}
                      clientName={p.client_name}
                      openTasks={p.open_tasks}
                      to={`/projects/${p.id}`}
                    />
                  ))}
                </ProjectCarousel>
            ) : (
              <>
                <div className="card-grid">
                  {data.projects.map((p) => (
                    <ProjectCard
                      key={p.id}
                      phase={p.phase}
                      dueLabel={formatDayMonth(
                        p.phase === 'shoot' ? (p.shoot_date ?? p.due_date) : p.due_date,
                      )}
                      name={p.name}
                      clientName={p.client_name}
                      openTasks={p.open_tasks}
                      to={`/projects/${p.id}`}
                    />
                  ))}
                </div>
                {counts.on_hold_count > 0 && (
                  <Link className="overview-hold-link" to="/projects?phase=on_hold">
                    <span className="phase-dot pd-hold" />
                    <span>{counts.on_hold_count} on hold</span>
                  </Link>
                )}
              </>
            )}
          </Group>

          <div className="group two-up" style={{ marginTop: 34 }}>
            <div>
              <div className="group-head">
                <span className="group-title">Client dashboards</span>
                <span className="group-count">{data.dashboards?.length ?? 0}</span>
                <GroupLink to="/dashboards">Manage →</GroupLink>
              </div>
              {(data.dashboards ?? []).map((d) => (
                <Link className="row" key={d.slug} to={`/dashboards/${d.slug}`}>
                  <span className="row-title" title={d.client_name}>{d.client_name}</span>
                  <span className="dash-slug">/{d.slug}</span>
                  <span className="row-spacer" />
                  <Pill
                    tone={
                      d.current_phase_label === 'Kickoff' || d.current_phase_label === 'Delivered'
                        ? undefined
                        : 'accent'
                    }
                  >
                    {d.current_phase_label}
                  </Pill>
                </Link>
              ))}
              {data.dashboards_status === 'unavailable' ? (
                <div className="empty-note">Dashboard stats unavailable.</div>
              ) : (data.dashboards?.length ?? 0) === 0 && (
                <div className="empty-note">No client dashboards yet.</div>
              )}
            </div>

            <div>
              <div className="group-head">
                <span className="group-title">Activity</span>
              </div>
              {data.activity.slice(0, 8).map((a) => (
                <ActivityRow key={a.id} time={relTime(a.created_at)}>
                  {a.summary}
                </ActivityRow>
              ))}
              {data.activity.length === 0 && <div className="empty-note">No activity yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
